import { computed } from 'vue';
import { useAppStore } from '@/stores/AppStore';

/**
 * List of billing/dollar amount field names that should be hidden from non-billing users.
 * This list should match the backend BillingFieldFilter.
 */
export const BILLING_FIELDS = new Set([
    // Request model
    'EstimatedAmount',
    'InvoiceTotal',
    'ShouldBillTotal',
    
    // RequestTask model
    'InvoiceAmount',
    'ShouldBill',
    'PaidAmount',
    
    // RequestPart model
    'PartsCost',
    'MSRP',
    
    // RequestExpense model
    'ExpenseAmount',
    
    // WorkOrder model
    'Credit',
    'Subtotal',
    'Tax',
    'Total',
    
    // WorkOrderItem model
    'UnitCost',
    'LineTotal',
    'DiscountAmount',
    'TaxAmount',
    
    // TaskClock model
    'Rate',
    
    // PM model
    'EstimatedCost',
    'ActualCost',
]);

/**
 * Composable for billing role checks and field visibility
 */
export function useBilling() {
    const appStore = useAppStore();

    /**
     * Whether the current user has billing access (Billing or Admin role)
     */
    const hasBillingAccess = computed(() => {
        const rolesRecord = appStore.account.user?.Roles;
        if (!rolesRecord) return false;
        
        // Roles is a Record<string, RoleModel>, check if any role name matches
        return Object.values(rolesRecord).some(role => {
            const roleName = role?.Name?.toLowerCase() || '';
            return roleName === 'billing' || roleName === 'admin';
        });
    });

    /**
     * Check if a specific field should be visible based on billing access
     */
    const canViewField = (fieldName: string): boolean => {
        if (hasBillingAccess.value) return true;
        return !BILLING_FIELDS.has(fieldName);
    };

    /**
     * Check if a field is a billing field
     */
    const isBillingField = (fieldName: string): boolean => {
        return BILLING_FIELDS.has(fieldName);
    };

    /**
     * Filter an array of column definitions to exclude billing columns
     */
    const filterBillingColumns = <T extends { field?: string; key?: string }>(columns: T[]): T[] => {
        if (hasBillingAccess.value) return columns;
        return columns.filter(col => {
            const fieldName = col.field || col.key || '';
            return !BILLING_FIELDS.has(fieldName);
        });
    };

    /**
     * Format currency value - returns empty string if user doesn't have billing access
     */
    const formatCurrency = (value: number | null | undefined, fallback: string = ''): string => {
        if (!hasBillingAccess.value) return fallback;
        if (value == null) return fallback;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    return {
        hasBillingAccess,
        canViewField,
        isBillingField,
        filterBillingColumns,
        formatCurrency,
        BILLING_FIELDS
    };
}

export default useBilling;
